import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { fmtEUR, todayISO, addDaysISO } from "../lib/format";
import { toast } from "sonner";

const emptyItem = (tva = 10) => ({
  description: "", depart: "", arrivee: "", date_course: "", heure_course: "", distance_km: 0,
  quantity: 1, prix_unitaire_ttc: 0, taux_tva: tva,
});

const ttcToHt = (ttc, tva) => {
  const t = Number(tva || 0);
  return Math.round((Number(ttc || 0) / (1 + t / 100)) * 100) / 100;
};
const htToTtc = (ht, tva) => {
  const t = Number(tva || 0);
  return Math.round(Number(ht || 0) * (1 + t / 100) * 100) / 100;
};

export const DocumentDialog = ({ open, onOpenChange, type, clients, editingDoc, defaultTva = 10, onSaved, apiCreate, apiUpdate }) => {
  const isInvoice = type === "invoice";
  const [clientId, setClientId] = useState("");
  const [dateEmission, setDateEmission] = useState(todayISO());
  const [dateEcheance, setDateEcheance] = useState(addDaysISO(30));
  const [status, setStatus] = useState(isInvoice ? "brouillon" : "brouillon");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([emptyItem(defaultTva)]);
  const [saving, setSaving] = useState(false);
  const [customNumber, setCustomNumber] = useState("");

  useEffect(() => {
    if (open) {
      if (editingDoc) {
        setClientId(editingDoc.client_id || "");
        setDateEmission(editingDoc.date_emission || todayISO());
        setDateEcheance(editingDoc.date_echeance || "");
        setStatus(editingDoc.status || "brouillon");
        setNotes(editingDoc.notes || "");
        setItems(editingDoc.line_items?.length
          ? editingDoc.line_items.map((i) => ({
              ...i,
              prix_unitaire_ttc: i.prix_unitaire_ttc != null
                ? i.prix_unitaire_ttc
                : htToTtc(i.prix_unitaire_ht, i.taux_tva),
            }))
          : [emptyItem(defaultTva)]);
      } else {
        setCustomNumber("");
        setClientId("");
        setDateEmission(todayISO());
        setDateEcheance(isInvoice ? addDaysISO(30) : addDaysISO(20));
        setStatus("brouillon");
        setNotes("");
        setItems([emptyItem(defaultTva)]);
      }
    }
  }, [open, editingDoc, defaultTva, isInvoice]);

  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };
  const addItem = () => setItems((p) => [...p, emptyItem(defaultTva)]);
  const removeItem = (idx) => setItems((p) => (p.length > 1 ? p.filter((_, i) => i !== idx) : p));

  const totals = items.reduce(
    (acc, it) => {
      const lineTtc = Number(it.quantity || 1) * Number(it.prix_unitaire_ttc || 0);
      const lineHt = lineTtc / (1 + Number(it.taux_tva || 0) / 100);
      acc.ht += lineHt;
      acc.tva += lineTtc - lineHt;
      return acc;
    },
    { ht: 0, tva: 0 }
  );
  const ttc = totals.ht + totals.tva;

  const statusOptions = isInvoice
    ? [["brouillon", "À transmettre"], ["envoyee", "Envoyée"], ["payee", "Payée"], ["retard", "En retard"]]
    : [["brouillon", "À transmettre"], ["envoye", "Envoyé"], ["accepte", "Accepté"], ["refuse", "Refusé"]];

  const submit = async () => {
    if (!clientId) return toast.error("Sélectionnez un client");
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        date_emission: dateEmission,
        date_echeance: dateEcheance,
        line_items: items.map((it) => ({
          ...it,
          distance_km: Number(it.distance_km || 0),
          quantity: Number(it.quantity || 1),
          prix_unitaire_ttc: Number(it.prix_unitaire_ttc || 0),
          prix_unitaire_ht: ttcToHt(it.prix_unitaire_ttc, it.taux_tva),
          taux_tva: Number(it.taux_tva || 0),
        })),
        notes,
        status,
        ...(!editingDoc && isInvoice && customNumber.trim() ? { number: customNumber.trim() } : {}),
      };
      if (editingDoc) await apiUpdate(editingDoc.id, payload);
      else await apiCreate(payload);
      toast.success(isInvoice ? "Facture enregistrée" : "Devis enregistré");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="document-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editingDoc ? "Modifier" : "Nouveau"} {isInvoice ? "facture" : "devis"}
          </DialogTitle>
          <DialogDescription>
            Sélectionnez le client, ajoutez les prestations/courses et saisissez le prix TTC. Le HT et la TVA sont calculés automatiquement.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-4 gap-4">
          <div className="sm:col-span-2 space-y-1.5">
            <Label>Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="doc-client-select"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Date d'émission</Label>
            <Input type="date" value={dateEmission} onChange={(e) => setDateEmission(e.target.value)} data-testid="doc-date-emission" />
          </div>
          <div className="space-y-1.5">
            <Label>{isInvoice ? "Échéance" : "Valide jusqu'au"}</Label>
            <Input type="date" value={dateEcheance} onChange={(e) => setDateEcheance(e.target.value)} data-testid="doc-date-echeance" />
          </div>
          {isInvoice && !editingDoc && (
            <div className="sm:col-span-2 space-y-1.5">
              <Label>N° de facture (optionnel)</Label>
              <Input placeholder="Automatique — ou ex : FAC-2026-0004" value={customNumber} onChange={(e) => setCustomNumber(e.target.value)} data-testid="doc-custom-number" />
              <p className="text-[11px] text-slate-500">Laissez vide pour la numérotation automatique. Remplissez pour recréer une facture manquante.</p>
            </div>
          )}
        </div>

        {/* Line items */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Prestations / Courses</Label>
            <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="doc-add-item-btn">
              <Plus className="h-4 w-4 mr-1" /> Ligne
            </Button>
          </div>
          <div className="space-y-3">
            {items.map((it, idx) => {
              const lineTtc = Number(it.quantity || 1) * Number(it.prix_unitaire_ttc || 0);
              return (
                <div key={idx} className="rounded-xl border border-slate-200 p-3 bg-slate-50/60" data-testid={`doc-item-${idx}`}>
                  <div className="grid sm:grid-cols-12 gap-2">
                    <Input className="sm:col-span-12 bg-white" placeholder="Description (ex: Transfert aéroport)" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} data-testid={`item-desc-${idx}`} />
                    <Input className="sm:col-span-4 bg-white" placeholder="Départ" value={it.depart} onChange={(e) => updateItem(idx, "depart", e.target.value)} />
                    <Input className="sm:col-span-4 bg-white" placeholder="Arrivée" value={it.arrivee} onChange={(e) => updateItem(idx, "arrivee", e.target.value)} />
                    <Input className="sm:col-span-2 bg-white" type="date" value={it.date_course} onChange={(e) => updateItem(idx, "date_course", e.target.value)} data-testid={`item-date-${idx}`} />
                    <Input className="sm:col-span-2 bg-white" type="time" placeholder="Heure" value={it.heure_course} onChange={(e) => updateItem(idx, "heure_course", e.target.value)} data-testid={`item-time-${idx}`} />
                  </div>
                  <div className="grid sm:grid-cols-12 gap-2 mt-2 items-center">
                    <div className="sm:col-span-2">
                      <Label className="text-[11px] text-slate-500">Quantité</Label>
                      <Input className="bg-white" type="number" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`item-qty-${idx}`} />
                    </div>
                    <div className="sm:col-span-3">
                      <Label className="text-[11px] text-slate-500">P.U. TTC (€)</Label>
                      <Input className="bg-white" type="number" step="0.01" value={it.prix_unitaire_ttc} onChange={(e) => updateItem(idx, "prix_unitaire_ttc", e.target.value)} data-testid={`item-price-${idx}`} />
                    </div>
                    <div className="sm:col-span-3">
                      <Label className="text-[11px] text-slate-500">TVA</Label>
                      <Select value={String(it.taux_tva)} onValueChange={(v) => updateItem(idx, "taux_tva", v)}>
                        <SelectTrigger className="bg-white" data-testid={`item-tva-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">0% (exonéré)</SelectItem>
                          <SelectItem value="10">10% (transport)</SelectItem>
                          <SelectItem value="20">20% (prestation)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-3 text-right">
                      <Label className="text-[11px] text-slate-500 block">Total TTC</Label>
                      <span className="font-mono font-semibold text-slate-900">{fmtEUR(lineTtc)}</span>
                    </div>
                    <div className="sm:col-span-1 flex justify-end">
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} data-testid={`item-remove-${idx}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Statut</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="doc-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {statusOptions.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Mentions particulières..." data-testid="doc-notes" />
            </div>
          </div>
          <div className="rounded-xl bg-slate-900 text-white p-5 self-start">
            <div className="text-[11px] text-slate-400 mb-2">Prix saisis en TTC · ventilation automatique</div>
            <div className="flex justify-between text-sm py-1"><span className="text-slate-300">Total HT</span><span className="font-mono">{fmtEUR(totals.ht)}</span></div>
            <div className="flex justify-between text-sm py-1"><span className="text-slate-300">TVA</span><span className="font-mono">{fmtEUR(totals.tva)}</span></div>
            <div className="h-px bg-slate-700 my-2" />
            <div className="flex justify-between items-center"><span className="text-[#C5A059] font-semibold">Total TTC</span><span className="font-mono text-xl font-bold text-[#C5A059]" data-testid="doc-total-ttc">{fmtEUR(ttc)}</span></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="doc-cancel-btn">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="doc-save-btn">
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
